import {Box3, Vector3} from "three";
import * as Globals from "../../globals";
import {HBESectionWidth} from "../../globals";
import {Roof} from "./roof";
import {buildingGroup, tmpGroup} from "../../main";
import {
    addBoundingBox,
    copyVectorArray,
    findChildrenByName,
    getPointsFromBoundingBox,
    getPointsFromBoundingBox2D
} from "../../utils/threeUtils";
import {clearGroup} from "../../sceneManager";
import {getMTEMeshFromThickness, getWallGroupParent} from "../../utils/configuratorUtils";
import {Attika} from "./attika";
import {createExtendedWallSegment, createWallSegment} from "../walls/wall";
import {
    createShapeFromPoints,
    getMeshWithHoleFromShape,
    pathPointsClockwise,
    shapeContainsPoints
} from "../../utils/shapeUtils";
import {maxAllowedRoofElementLength, maxFreeOverhangWidth} from "../../constants/flatRoofConstants";
import {RoofOrientation} from "../../constants/roofConstants";
import {degrees90} from "../../constants/mathConstants";
import {toFixedFloat, toFixedVector} from "../../utils/utils";
import {raehmMeshName, supportBeamMeshName} from "../../constants/nameConstants";
import {showMessage} from "../../../ui/ui";
import {RoofSectionsCalculator} from "../../calculators/roof/flatRoof/roofSectionsCalculator";
import {RoofConfigCalculator} from "../../calculators/roof/flatRoof/roofConfigCalculator";
import {createRooms} from "../rooms/roomGeneration";

/*
 * TODO: Dachüberstand von über 1m Buggy.
 *  Schneidetool ist zu klein > Schnitt invertiert. Tools vergrößern: Dach Bounds + Überstand + Toleranz
 *  Aktuell noch nicht relevant weil maximaler Dachüberstand eine halbe Elementbreite ist. Erst wichtig wenn X & Z Richtung unterschiedliche Dachüberstände haben können
 */

export class FlatRoof extends Roof {
    constructor(overhang, orientation, addAttika, attikaHeight = 0, roofPoints = [], roofPointsObjects = []) {
        super(overhang, orientation, roofPoints, roofPointsObjects);
        this.addAttika = addAttika;
        this.attikaHeight = attikaHeight;
        this.offset = 0; //Offset des Dachs entgegen der Laufrichtung: #12742 letztes Element darf maximal 15cm breit sein wenn nicht gestützt.
    }

    create(regenerated = false) {
        let roof;
        let raehme = findChildrenByName(buildingGroup, raehmMeshName, false, true);
        raehme = this.filterRaehmeOutsideBuilding(raehme);
        let supportBeams = findChildrenByName(buildingGroup, supportBeamMeshName, false, true);
        for (let i = 0; i < raehme.length; i++) {
            raehme[i].updateMatrix();
            addBoundingBox(raehme[i], tmpGroup);
        }
        for (let i = 0; i < supportBeams.length; i++) {
            supportBeams[i].updateMatrix();
            addBoundingBox(supportBeams[i], tmpGroup);
        }

        let roofConfigCalculator;
        try {
            createRooms();
            roofConfigCalculator = new RoofConfigCalculator();
        } catch (e) {
            console.log(e);
            showMessage(e.message);
            return;
        }

        if (roofConfigCalculator === null) {
            return;
        }

        let orientedRoof = this.createWithOrientation(roofConfigCalculator.orientation, false);
        let roofConfiguration = {
            roof: orientedRoof,
            orientation: roofConfigCalculator.orientation,
            thickness: roofConfigCalculator.thickness,
            overhang: this.overhang
        };

        let sections;
        try {
            sections = new RoofSectionsCalculator(roofConfiguration, [...raehme].concat(supportBeams));
        } catch (e) {
            showMessage(e.message);
            console.error(e);
            clearGroup(tmpGroup);
            return;
        } finally {
            clearGroup(roofConfiguration.roof);
            roofConfiguration.roof.removeFromParent();
        }

        //Berechne ggf. notwendiges Offset des Daches um überstehende Element befestigen zu können
        //Standardmäßig kein Offset: Wenn kein Dachüberstand vorhanden ist, oder das Endstück ausreichend gestützt wird.
        if (this.overhang > 0) {
            //Berechne größe des Endstücks
            let roofValues = this.getRoofValues(roofConfigCalculator.orientation);
            let totalWidth = toFixedFloat(roofValues.width + (2 * this.overhang));
            let lastElementWidth = toFixedFloat(totalWidth % HBESectionWidth);
            //Wenn das letzte Element mindestens zur Hälfte gestützt ist, besteht kein Bedarf für ein Offset
            if (!(lastElementWidth >= (2 * this.overhang))) {
                if (lastElementWidth > maxFreeOverhangWidth) {
                    this.offset = 0.5 * lastElementWidth;
                }
            }
        }

        this.actualOrientation = roofConfigCalculator.orientation; //Tatsächlich genutzte Rotation aus berechneter Konfiguration
        roof = this.createFinalRoof(sections, roofConfigCalculator);
        this.setUserData(roof, regenerated);
        buildingGroup.addWithUuid(roof); //Einfügen der Decke

        clearGroup(tmpGroup);

        if (this.addAttika) {
            this.createAttika(roof);
        }

        return roof;
    }

    createWithOrientation(orientation, withOverhang = false, predefinedSections = []) {
        let previewBounds = new Box3().setFromObject(this.previewShape);
        let roofValues = this.getRoofValues(orientation);
        let rotate = roofValues.rotate;
        let cutMesh = this.createCuttingMesh(rotate, withOverhang);

        if (withOverhang) {
            roofValues.width += 2 * this.overhang;
            roofValues.depth += 2 * this.overhang;
        }

        if (this.offset > 0) {
            //Wenn ein Offset benötigt wird muss ein zusätzliches Element erzeugt werden um anschließend den Schnitt so zu positionieren das am Start und Ende des Dachs ein Endstück entsteht
            roofValues.width += HBESectionWidth;
        }

        let wallMesh = getMTEMeshFromThickness(this.thickness);

        //Dachelement erzeugen
        let roof;
        if (roofValues.depth > maxAllowedRoofElementLength) {
            if (predefinedSections.length === 0) {
                predefinedSections = this.getInitialRoofPartDepths(roofValues.depth);
            }
            roof = createExtendedWallSegment(roofValues.width, predefinedSections, wallMesh, Globals.layers.roof);
        } else {
            roof = createWallSegment(roofValues.width, roofValues.depth, wallMesh, Globals.layers.roof, false);
        }

        if (this.offset > 0) {
            //Offset wieder rausrechnen für Speichern der userData etc.
            roofValues.width -= HBESectionWidth;
            for (let i = 0; i < roof.children.length; i++) {
                roof.children[i].translateX(-(0.5 * HBESectionWidth) + this.offset);
                if (roof.children[i].type === "Mesh" && roof.children[i].geometry !== undefined) {
                    try {
                        roof.children[i].geometry.computeTangents();
                        roof.children[i].geometry.computeVertexNormals();
                        roof.children[i].geometry.normalizeNormals();
                    } catch (e) {
                        console.warn(e);
                    }
                }
            }
        }

        roof.name = "roofGroup";
        roof.userData.width = roofValues.width;
        roof.userData.depth = roofValues.depth;

        //Zuschnitt
        this.cutRoofElementsToShape(roof, cutMesh);
        cutMesh.geometry.dispose();

        if (rotate) {
            roof.rotateY(degrees90);
        }

        //Positionierung des Dachs
        let previewCenter = previewBounds.getCenter(new Vector3());
        roof.position.set(previewCenter.x, this.getRoofHeight(), previewCenter.z);
        return roof;
    }

    getInitialRoofPartDepths(fullDepth) {
        //Vorabkalkulation der Elementlängen zur Erstellung eines temporären Dachs.
        //Annahme das immer das Element auf volle Länge gebracht wird und dann ein neues angefangen wird.
        //Die tatsächlichen Teillängen werden später basierend auf den Spannweiten und Positionen berechnet
        let fullParts = Math.floor(fullDepth / maxAllowedRoofElementLength);
        let lastPart = fullDepth % maxAllowedRoofElementLength;
        let parts = [];
        for (let i = 0; i < fullParts; i++) {
            parts.push(maxAllowedRoofElementLength);
        }
        parts.push(lastPart);
        return parts;
    }

    getRoofValues(orientation) {
        let previewBounds = new Box3().setFromObject(this.previewShape);
        let previewSize = previewBounds.getSize(new Vector3());
        let depth, width;
        let rotate = false;

        if (orientation === RoofOrientation.XAxis) {
            rotate = true;
            width = previewSize.z;
            depth = previewSize.x;
        } else {
            width = previewSize.x;
            depth = previewSize.z;
        }

        return {
            width: width, //Kurze Seite
            depth: depth, //Lange Seite
            rotate: rotate
        };
    }

    createCuttingMesh(rotate, withOverhang = false) {
        this.previewShape.removeFromParent();
        let previewBounds = new Box3().setFromObject(this.previewShape);
        let cutMeshBounds = new Box3().copy(previewBounds).expandByScalar(1.02);
        let points = getPointsFromBoundingBox(cutMeshBounds, true);
        let cutShape = createShapeFromPoints(points);
        let roofPoints = this.roofPoints;
        if (withOverhang) {
            let newRoofPoints = copyVectorArray(this.roofPoints);
            let roofPointsClockwise = pathPointsClockwise(newRoofPoints);
            roofPoints = Roof.extendRoofPointsByLength(newRoofPoints, roofPointsClockwise, this.overhang);
        }

        return getMeshWithHoleFromShape(cutShape, roofPoints, rotate);
    }

    createFinalRoof(sections, optimalRoofConfig) {
        this.thickness = optimalRoofConfig.thickness;

        console.log(
            "Optimales Dach\n" +
            "==============\n" +
            "Optimale Orientierung: " + (parseInt(optimalRoofConfig.orientation) === RoofOrientation.XAxis ? "X" : "Z") + "\n" +
            "Errechnete Dachstärke: " + optimalRoofConfig.thickness + "m."
        );

        //Erzeugung eines Dachs mit berechneter Deckenstärke und Orientierung
        return this.createWithOrientation(optimalRoofConfig.orientation, true, sections);
    }

    setUserData(roof, regenerated) {
        roof.userData.roof = {
            points: copyVectorArray(this.roofPoints, true), //Dachpunkte für spätere Neugenerierung o.ä abspeichern
            orientation: this.orientation,
            autoOrientation: this.actualOrientation,
            thickness: this.thickness,
            overhang: this.overhang,
            addAttika: this.addAttika,
            attikaHeight: this.attikaHeight,
            type: 1,
            automatic: this.automaticRoof
        };

        if (!this.automaticRoof && !regenerated) {
            for (let i = 0; i < this.roofPoints.length; i++) {
                let element = getWallGroupParent(this.roofPointObjects[i]);
                if (element === null) {
                    continue; //TODO: Tritt bei Unterzüge auf wenn manuell dach darüber gezogen wird
                }
                if (element.userData.roof === undefined) {
                    element.userData.roof = {roofPoints: []};
                }

                if (element.userData.roof.roofPoints === undefined) {
                    element.userData.roof.roofPoints = [];
                }

                let point = this.roofPoints[i];
                element.userData.roof.roofPoints.push({index: i, point: toFixedVector(point)});
            }
        }
    }

    createAttika(roof) {
        let newRoofPoints = copyVectorArray(this.roofPoints);
        let roofPointsClockwise = pathPointsClockwise(newRoofPoints);
        let roofPointsAttika = Roof.extendRoofPointsByLength(newRoofPoints, roofPointsClockwise, this.overhang);
        new Attika(roof, roofPointsAttika, this.attikaHeight).create();
    }


    //Helper
    filterRaehmeOutsideBuilding(allRaehmMeshes) {
        //TODO: Lässt sich noch optimieren
        //Rähme filtern die nicht innerhalb der roofPoints liegen
        let newRoofPoints = copyVectorArray(this.roofPoints);
        let roofPointsClockwise = pathPointsClockwise(newRoofPoints);
        newRoofPoints = Roof.extendRoofPointsByLength(newRoofPoints, roofPointsClockwise, 0.005); //Vergrößern um minimale Toleranz damit die Außenwände nicht entfernt werden
        let shape = createShapeFromPoints(newRoofPoints);
        for (let i = allRaehmMeshes.length - 1; i >= 0; i--) {
            let mesh = allRaehmMeshes[i];
            mesh.updateMatrix();
            let bounds = new Box3().setFromObject(mesh.parent);
            let points = getPointsFromBoundingBox2D(bounds);
            let included = true;
            for (let j = 0; j < points.length; j++) {
                if (!shapeContainsPoints(shape, points[j])) {
                    included = false;
                    break;
                }
            }

            if (!included) {
                allRaehmMeshes.splice(i, 1);
            }
        }

        return allRaehmMeshes;
    }
}
